import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AvGroup, AvInput } from 'availity-reactstrap-validation';
import { FormattedMessage, useIntl } from 'react-intl';
import { Label } from 'reactstrap';
import PropTypes from 'prop-types';
import Flatpickr from 'react-flatpickr';
import classnames from 'classnames';
import {
  capitalize,
  getCurrentAtDate,
} from '../../../../utility/util/util.service';
import { DateTime } from 'luxon';
import { customMessageNotification } from '../../../../utility/notification/notification.service';
import { createInvalidDateNotifier } from './date-time-field.notification';

/**
 * The DateTimeField component.
 *
 * Caution: This Field can be used controlled (redux, state, ...) or uncontrolled (html form). You need to decide and use only one!
 *
 * @param {string|Date} value - The value to be used initially. If passed, object[propertyName] is ignored. If falsy value is passed, the input will be reset.
 * @param {string} propertyName - The name of the Property (will be used for name in Form) (required).
 * @param {Object} object - Used to extract the value with object[propertyName] from object.
 * @param {function} onChange - Returns the value after it has been changed. WIll not be triggered if date is invalid.
 * @param {string} className - The CSS Classes.
 * @param {Object} options - The Flatpickr options (https://flatpickr.js.org/options/ or https://github.com/flatpickr/flatpickr/blob/master/src/types/options.ts).
 * @param {Object[]} serviceTimes - Array of ServiceTimes. Selected DateTime will be validated if passed.
 * @param {Object} [store] - Will show a "Store is closed" Notification if no satisfied ServiceTime was found
 * @param {string} labelMessageId - The Message ID for the label.
 * @param {boolean} [required] - Whether the field can be skipped (value="") or not.
 * @param {string} [dataTestId] - The Test ID to be passed to the hidden value field.
 * @param {string} [placeholder] - The Placeholder
 * @param {boolean} [showRelativeTime] - Whether to show relative time or not
 * @param {function} [additionalValidation] - Optional predicate (date) => boolean ANDed into the date validity (e.g. to validate against OrderCondition service times).
 * @param {string} [additionalInvalidMessageId] - Message ID for the notification shown when additionalValidation fails (and the store is open).
 * @param {...Object} props - Other properties to pass to flatpickr.
 *
 * @returns {ReactElement} The rendered DateTimeField component.
 */
const DateTimeField = ({
  value,
  propertyName,
  object,
  onChange,
  className,
  options,
  serviceTimes,
  store,
  labelMessageId,
  required,
  dataTestId,
  showRelativeTime,
  placeholder,
  setIsDateTimeFieldValid,
  wrapperClassName,
  autoPlacement,
  additionalValidation,
  additionalInvalidMessageId,
  ...props
}) => {
  const intl = useIntl();
  const [selectedDate, setSelectedDate] = useState(null);
  const flatpickrRef = useRef();
  const flatpickr = flatpickrRef?.current?.flatpickr;
  const inputFieldRef = useRef();
  const inputFieldFormCtrl = inputFieldRef?.current?.context?.FormCtrl;
  const mergedOptions = {
    // locale is set globally in IntlContext
    minuteIncrement: 10,
    allowInput: false,
    enableTime: true,
    enableSeconds: false,
    noCalendar: false, // set this and enableTime to true to enable time-only picker
    monthSelectorType: 'static',
    position: autoPlacement ? 'auto left' : 'auto center',
    disableMobile: true, // mobile is weird...
    ...options,
  };

  // set the selectedDate when values/object changes
  useEffect(() => {
    if (flatpickr) {
      let dateValue = value || object?.[propertyName];

      // Check if the value is a time string in the format 'HH:mm'
      if (typeof dateValue === 'string' && /^\d{2}:\d{2}$/.test(dateValue)) {
        // Convert the 'HH:mm' time string to a DateTime object for today's date
        dateValue = DateTime.fromFormat(dateValue, 'HH:mm')
          .set({
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            day: new Date().getDate(),
          })
          .toJSDate();
      }

      const date = new Date(dateValue);

      if (DateTime.fromJSDate(date).isValid) {
        setSelectedDate(date);

        if (flatpickr.selectedDates?.[0]) {
          const currentDate = flatpickr.selectedDates[0];
          const toISO = (date) =>
            DateTime.fromJSDate(date)
              .startOf('second')
              .toISO();

          if (toISO(date) !== toISO(currentDate)) {
            flatpickr.setDate(date);
          }
        } else {
          flatpickr.setDate(date); // Set the initial date if flatpickr has no selected date
        }
        const isValid = isDateValid(date);
        setIsFlatpickerInvalid(!isValid);
        setIsFormFieldInvalid(!isValid);
      } else {
        setIsFlatpickerInvalid(false);
        setIsFormFieldInvalid(false);
        // keep the displayed value in sync with the cleared input, otherwise a
        // warning queued for the previous date would still find it "selected"
        setSelectedDate(null);
        flatpickr.clear();
      }
    }
  }, [value, object?.[propertyName], flatpickr]);

  const isDateValid = (date) => {
    return date
      ? (!mergedOptions.minDate || mergedOptions.minDate <= date) &&
          (!mergedOptions.maxDate || mergedOptions.maxDate >= date) &&
          (!serviceTimes?.length || !!getCurrentAtDate(serviceTimes, date)) &&
          (!additionalValidation || additionalValidation(date))
      : !required;
  };

  const setIsFormFieldInvalid = (isInvalid) => {
    if (!inputFieldFormCtrl) return;
    inputFieldFormCtrl.setBad(propertyName, isInvalid);
  };

  const setIsFlatpickerInvalid = (isInvalid) => {
    if (!flatpickr || !inputFieldFormCtrl?.isTouched(propertyName)) return;
    const classList = (flatpickr.isMobile
      ? flatpickr.mobileInput // since we're using disableMobile=true, this is left here for reference. It will never be this.
      : flatpickr.input
    ).classList;

    if (!classList) throw new Error('Flatpicker classList not available');

    if (isInvalid) {
      classList.add('is-invalid');
    } else {
      classList.remove('is-invalid');
    }
  };

  // A warning must not outlive the value that caused it. Every pick while the
  // picker is being scrolled is reported, and a trailing-edge debounce that is
  // never cancelled announced an invalid pick ~1s after the customer had
  // already moved to a valid time. So the notifier re-checks before firing:
  // the exact time it warned about must still be the one on screen, and must
  // still be invalid.
  const selectedDateRef = useRef(null);
  selectedDateRef.current = selectedDate;

  const isDateValidRef = useRef(null);
  isDateValidRef.current = isDateValid;

  const notifierRef = useRef(null);
  if (!notifierRef.current) {
    const isStillInvalid = (date) => {
      const shown = selectedDateRef.current;
      if (!shown || !date || shown.getTime() !== date.getTime()) return false;
      return !isDateValidRef.current(date);
    };
    notifierRef.current = createInvalidDateNotifier({
      notify: (message) => customMessageNotification('warn', message),
      stillInvalid: isStillInvalid,
    });
  }

  const notifier = notifierRef.current;

  // a pending warning must not survive the field
  useEffect(() => () => notifier.cancel(), [notifier]);

  const warnStoreClosed = useCallback(
    (date) =>
      notifier.schedule(
        date,
        intl.formatMessage(
          { id: 'storeClosedAtRequestedTimeError' },
          { storeName: store?.name },
        ),
      ),
    [notifier, intl, store?.name],
  );

  const warnAdditionalInvalid = useCallback(
    (date) => {
      if (!additionalInvalidMessageId) return;
      notifier.schedule(
        date,
        intl.formatMessage(
          { id: additionalInvalidMessageId },
          { storeName: store?.name },
        ),
      );
    },
    [notifier, additionalInvalidMessageId, intl, store?.name],
  );

  // if showRelativeTime=true, we need to periodically update the Flatpickr component, because time flies.
  useEffect(() => {
    if (showRelativeTime) {
      const id = setInterval(() => flatpickr?.updateValue(), 60000);
      return () => clearInterval(id);
    }
  }, [showRelativeTime]);

  return (
    <AvGroup
      className={`form-label-group mb-0 ${wrapperClassName}`}
      onFocus={() => inputFieldFormCtrl?.setTouched(propertyName)} // simulate that the (hidden) input field has been touched (for validation purposes)
    >
      <Flatpickr
        ref={flatpickrRef}
        className={classnames('form-control p-50 no-icon', className)}
        placeholder={placeholder}
        options={{
          formatDate: (date) => {
            const { noCalendar, enableTime, enableSeconds } =
              mergedOptions || {};

            const formatRelative = (date) => {
              const now = DateTime.now();
              const dateTime = DateTime.fromJSDate(date);
              const timeSuffix = dateTime.toLocaleString(DateTime.TIME_SIMPLE);
              const diffToNow = dateTime.diff(now, 'minutes');

              if (diffToNow.minutes <= 60) {
                return intl.formatRelativeTime(
                  Math.round(diffToNow.minutes),
                  'minute',
                );
              } else if (dateTime < now.endOf('day').plus({ days: 1 })) {
                const dowPrefix = capitalize(dateTime.toRelativeCalendar());
                return `${dowPrefix}, ${timeSuffix}`;
              } else if (dateTime < now.endOf('week')) {
                const dowPrefix = dateTime.toFormat('cccc');
                return `${dowPrefix}, ${timeSuffix}`;
              } else {
                return dateTime.toLocaleString(
                  DateTime.DATETIME_MED_WITH_WEEKDAY,
                );
              }
            };

            return showRelativeTime
              ? formatRelative(date)
              : intl.formatDate(date, {
                  format:
                    noCalendar && enableTime
                      ? 'timeRegular'
                      : !noCalendar && enableTime
                      ? 'fullRegular'
                      : 'dateRegular',
                  second: enableSeconds ? '2-digit' : undefined,
                });
          },
          ...mergedOptions,
        }}
        onChange={(rawDate) => {
          const date = Array.isArray(rawDate) ? rawDate[0] : rawDate; // currently only one date is supported, although flatpickr supports more
          const isValid = isDateValid(date);

          setIsFlatpickerInvalid(!isValid);
          setIsFormFieldInvalid(!isValid);
          setSelectedDate(date);
          if (setIsDateTimeFieldValid) {
            setIsDateTimeFieldValid(true);
          }
          if (date) {
            const isStoreClosed =
              store && serviceTimes && !getCurrentAtDate(serviceTimes, date);
            const failsAdditionalValidation =
              !isStoreClosed &&
              additionalValidation &&
              !additionalValidation(date);

            if (isStoreClosed) {
              warnStoreClosed(date);
              if (setIsDateTimeFieldValid) {
                setIsDateTimeFieldValid(false);
              }
            } else if (failsAdditionalValidation) {
              warnAdditionalInvalid(date);
              if (setIsDateTimeFieldValid) {
                setIsDateTimeFieldValid(false);
              }
            } else {
              // a corrected pick supersedes any warning still queued for the
              // previous one
              notifier.cancel();
            }
          } else {
            notifier.cancel();
          }

          // onChange will only be triggered if the entered date is valid. Otherwise, we would e.g. update redux to have an invalid value, etc.
          if (onChange && isValid) onChange(date, isValid);
        }}
        {...props}
      />
      {labelMessageId && (
        <Label className="floating-label">
          <FormattedMessage id={labelMessageId} />
        </Label>
      )}
      <AvInput
        className="text-center"
        ref={inputFieldRef}
        data-testid={dataTestId}
        type="text"
        name={propertyName}
        value={selectedDate?.toISOString()}
        validate={{
          // do an additional check, in case the field is not required
          async: (dateString) => {
            const isValid = isDateValid(dateString && new Date(dateString));
            setIsFlatpickerInvalid(!isValid);

            // this form field will be marked as invalid as we return false
            return isValid;
          },
        }}
        hidden
      />
    </AvGroup>
  );
};

DateTimeField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  propertyName: PropTypes.string.isRequired,
  object: PropTypes.object,
  onChange: PropTypes.func,
  className: PropTypes.string,
  options: PropTypes.object,
  serviceTimes: PropTypes.array,
  store: PropTypes.object,
  labelMessageId: PropTypes.string,
  required: PropTypes.bool,
  dataTestId: PropTypes.string,
  showRelativeTime: PropTypes.bool,
  placeholder: PropTypes.string,
  additionalValidation: PropTypes.func,
  additionalInvalidMessageId: PropTypes.string,
  props: PropTypes.object,
};

export default DateTimeField;
